import { getUserData, getUserPriorityRole, canGlobalEdit, canGlobalView, userHasRole } from "../../Helper/commonFunctions_v2";

/**
 * Shared constants + helpers for GM-5485 User Access Review.
 */

export const REVIEW_STATUS = {
    OPEN: "OPEN",
    COMPLETED: "COMPLETED",
    CANCELLED: "CANCELLED",
};

export const ITEM_STATUS = {
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    CHANGE_REQUESTED: "CHANGE_REQUESTED",
};

export const ROLE_DECISION = {
    KEEP: "KEEP",
    REVOKE: "REVOKE",
};

export const REVIEW_STATUS_META = {
    OPEN: { label: "Open", tone: "blue" },
    COMPLETED: { label: "Completed", tone: "green" },
    CANCELLED: { label: "Cancelled", tone: "slate" },
};

export const ITEM_STATUS_META = {
    PENDING: { label: "Pending", tone: "amber" },
    APPROVED: { label: "Approved", tone: "green" },
    CHANGE_REQUESTED: { label: "Change requested", tone: "red" },
};

export const REVIEW_STATUS_FILTERS = [
    { value: "", label: "All reviews" },
    { value: REVIEW_STATUS.OPEN, label: "Open" },
    { value: REVIEW_STATUS.COMPLETED, label: "Completed" },
    { value: REVIEW_STATUS.CANCELLED, label: "Cancelled" },
];

export const ITEM_STATUS_FILTERS = [
    { value: "", label: "All items" },
    { value: ITEM_STATUS.PENDING, label: "Pending" },
    { value: ITEM_STATUS.APPROVED, label: "Approved" },
    { value: ITEM_STATUS.CHANGE_REQUESTED, label: "Change requested" },
];

export const PROOF_ACCEPT = ".eml,application/pdf,image/png,image/jpeg,message/rfc822";
export const PROOF_MAX_BYTES = 8 * 1024 * 1024;
export const PROOF_MAX_FILES = 5;

export const isReadOnlyGlobalViewer = () => canGlobalView() && !canGlobalEdit() && getUserPriorityRole() !== "hr";

export const canUseAccessReviewHr = () => {
    if (getUserPriorityRole() === "hr") return true;
    if (canGlobalEdit()) return true;
    if (userHasRole("superb_admin") || userHasRole("founder")) return true;
    if (isReadOnlyGlobalViewer()) return true;
    return false;
};

export const canWriteAccessReviewHr = () => {
    if (getUserPriorityRole() === "hr") return true;
    if (canGlobalEdit()) return true;
    if (userHasRole("superb_admin") || userHasRole("founder")) return true;
    return false;
};

/** HR / admin / global_editor may dismiss the manager pending-review block overlay. */
export const canBypassAccessReviewPendingBlock = () => userHasRole("hr") || userHasRole("admin") || userHasRole("global_editor");

export const canUseAccessReviewManager = () => {
    const data = getUserData() || {};
    return !!(data.id || data.userId || data.sub);
};

export const currentUserId = () => {
    const data = getUserData() || {};
    return data.id ?? data.userId ?? data.sub ?? null;
};

export const extractErrorMessage = (error, fallback = "Something went wrong. Please retry or contact support.") => error?.response?.data?.message || error?.message || fallback;

export const isRequestCancelled = (error) => {
    if (!error) return false;
    const msg = String(error?.message || "");
    return error?.__CANCEL__ === true || error?.code === "ERR_CANCELED" || msg === "Repeat request cancelled" || msg === "Preserving Back State";
};

export const formatBytes = (n) => {
    const bytes = Number(n) || 0;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDateTime = (value) => {
    if (value == null || value === "") return "—";
    const ms = typeof value === "number" ? value : Date.parse(value);
    if (!Number.isFinite(ms)) return "—";
    try {
        return new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return "—";
    }
};

export const formatDate = (value) => {
    if (value == null || value === "") return "—";
    const ms = typeof value === "number" ? value : Date.parse(value);
    if (!Number.isFinite(ms)) return "—";
    try {
        return new Date(ms).toLocaleDateString(undefined, { dateStyle: "medium" });
    } catch {
        return "—";
    }
};

export const snapshotDisplayName = (snapshot) => {
    if (!snapshot) return "—";
    const name = [snapshot.firstName || snapshot.first_name, snapshot.lastName || snapshot.last_name].filter(Boolean).join(" ");
    return name || snapshot.username || "—";
};

/** Prefer item.manager { id, name, username } from GET item; fall back to snapshot fields.
 * Returns { id, name, username, label } or null.
 */
export const itemManagerInfo = (item) => {
    const m = item?.manager;
    if (m && (m.name || m.username || m.id != null)) {
        const name = m.name || null;
        const username = m.username || null;
        const label = [name, username].filter(Boolean).join(" · ") || (m.id != null ? `User #${m.id}` : null);
        return { id: m.id ?? null, name, username, label: label || "—" };
    }
    const snapshot = item?.snapshot;
    const username = snapshot?.managerUsername || snapshot?.manager_username || null;
    const id = snapshot?.managerId ?? snapshot?.manager_id ?? null;
    if (!username && id == null) return null;
    return {
        id,
        name: null,
        username,
        label: username || (id != null ? `User #${id}` : "—"),
    };
};

/** Prefer item.reviewer { id, name, username } from item payload; fall back to reviewer_user_id. */
export const itemReviewerInfo = (item) => {
    const r = item?.reviewer;
    if (r && (r.name || r.username || r.id != null)) {
        const name = r.name || null;
        const username = r.username || null;
        const label = [name, username].filter(Boolean).join(" · ") || (r.id != null ? `User #${r.id}` : "—");
        return { id: r.id ?? null, name, username, label: label || "—" };
    }
    const id = item?.reviewer_user_id ?? null;
    if (id == null) return null;
    return { id, name: null, username: null, label: `User #${id}` };
};

export const AUDIT_EVENT_META = {
    REVIEW_OPENED: { label: "Review opened" },
    REVIEW_COMPLETED: { label: "Review completed" },
    REVIEW_CANCELLED: { label: "Review cancelled" },
    ITEM_REASSIGNED: { label: "Item reassigned" },
    ITEM_REOPENED: { label: "Item reopened" },
    ITEM_APPROVED: { label: "Access approved" },
    ITEM_CHANGE_REQUESTED: { label: "Change requested" },
};

/** Stable React key — attestation rows have id=null; use item_id + created_at. */
export const auditEventKey = (ev, index = 0) => {
    if (ev?.id != null) return String(ev.id);
    const itemId = ev?.item_id;
    const at = ev?.created_at || ev?.at;
    const type = ev?.event_type || ev?.type || "event";
    if (itemId != null && at) return `${itemId}-${at}-${type}`;
    return `audit-${index}`;
};

export const auditEventLabel = (ev) => {
    const type = ev?.event_type || ev?.type;
    if (!type) return "Event";
    if (AUDIT_EVENT_META[type]) return AUDIT_EVENT_META[type].label;
    return String(type)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const auditActorLabel = (ev) => ev?.actor_name || ev?.actor_username || ev?.actor || (ev?.actor_user_id != null ? `User #${ev.actor_user_id}` : "—");

const auditRoleDecisionLines = (decisions) => {
    if (!Array.isArray(decisions) || decisions.length === 0) return [];
    const revokes = decisions.filter((d) => d.decision === ROLE_DECISION.REVOKE);
    if (!revokes.length) return [];
    return revokes.map((d) => {
        const id = d.roleId ?? d.role_id;
        const name = d.roleName || d.role_name;
        return name || (id != null ? `Role #${id}` : "Role");
    });
};

/** Human-readable detail lines for audit-log entries (details map uses camelCase). */
export const formatAuditDetailLines = (ev) => {
    const type = ev?.event_type || ev?.type;
    const details = ev?.details;
    const lines = [];

    if (ev?.target && (ev.target.name || ev.target.username || ev.target.id != null)) {
        const t = ev.target;
        const primary = t.name || t.username || (t.id != null ? `User #${t.id}` : "—");
        const value = t.name && t.username ? `${t.name} (${t.username})` : primary;
        lines.push({ label: "Reportee", value });
    }

    if (ev?.item_id != null) {
        lines.push({ label: "Item", value: `#${ev.item_id}` });
    }

    if (details && typeof details === "object") {
        if (type === "ITEM_APPROVED") {
            lines.push({ label: "Outcome", value: "Access attested as appropriate" });
        }

        if (type === "ITEM_CHANGE_REQUESTED") {
            const requestType = details.requestType || details.request_type;
            if (requestType) lines.push({ label: "Request type", value: requestType });
            if (details.proposedOffboard) lines.push({ label: "Offboard", value: "Yes" });
            if (details.proposedNewManagerName) lines.push({ label: "New manager", value: details.proposedNewManagerName });
            const revokeLabels = auditRoleDecisionLines(details.roleDecisions);
            if (revokeLabels.length) lines.push({ label: "Roles to revoke", value: revokeLabels.join(", ") });
            if (details.raisedRequestId != null) lines.push({ label: "Lifecycle request", value: `#${details.raisedRequestId}` });
        }

        if (type === "ITEM_REOPENED") {
            const prevStatus = details.previousReviewStatus || details.previousStatus || details.previousDecision;
            if (prevStatus) lines.push({ label: "Previous status", value: prevStatus });
            const prevRequest = details.previousRequestType || details.requestType;
            if (prevRequest) lines.push({ label: "Previous request", value: prevRequest });
            const revokeLabels = auditRoleDecisionLines(details.roleDecisions || details.previousRoleDecisions);
            if (revokeLabels.length) lines.push({ label: "Previous revokes", value: revokeLabels.join(", ") });
            if (details.proposedOffboard || details.previousProposedOffboard) lines.push({ label: "Had offboard", value: "Yes" });
            if (details.proposedNewManagerName || details.previousProposedNewManagerName) {
                lines.push({ label: "Had new manager", value: details.proposedNewManagerName || details.previousProposedNewManagerName });
            }
        }

        if (type === "ITEM_REASSIGNED") {
            const nextReviewer = details.newReviewerName || details.reviewerName || details.new_reviewer_name;
            if (nextReviewer) lines.push({ label: "New reviewer", value: nextReviewer });
        }

        const source = details.reviewerSource || details.reviewer_source;
        if (source && type !== "ITEM_REOPENED") {
            lines.push({ label: "Reviewer source", value: REVIEWER_SOURCE_META[source]?.label || source });
        }
    }

    if (ev?.comment) {
        lines.push({ label: "Comment", value: ev.comment });
    } else if (typeof details === "string" && details.trim()) {
        lines.push({ label: "Details", value: details });
    }

    return lines;
};

export const snapshotRoles = (snapshot) => {
    if (!snapshot) return { direct: [], derived: [] };
    const direct = snapshot.directRoles || snapshot.direct_roles || [];
    const derived = snapshot.groupDerivedRoles || snapshot.group_derived_roles || [];
    return { direct, derived };
};

export const isSnapshotActive = (snapshot) => {
    const status = snapshot?.status;
    if (!status) return null;
    if (typeof status.active === "boolean") return status.active;
    return !status.softDelete && !status.isBlocked && status.isVerified !== false;
};

/** Map role id → display name from decisions + snapshot roles. */
export const roleNameLookup = (item) => {
    const map = {};
    const { direct, derived } = snapshotRoles(item?.snapshot);
    [...direct, ...derived].forEach((r) => {
        if (r?.id != null) map[r.id] = r.name || r.roleName || String(r.id);
    });
    (item?.role_decisions || []).forEach((d) => {
        const id = d.roleId ?? d.role_id;
        const name = d.roleName || d.role_name;
        if (id != null && name) map[id] = name;
    });
    return map;
};

/** Human-readable labels for roles_to_remove (prefer names over bare ids). */
export const rolesToRemoveLabels = (item) => {
    const ids = item?.roles_to_remove || [];
    if (!Array.isArray(ids) || ids.length === 0) return [];
    const names = roleNameLookup(item);
    return ids.map((id) => names[id] || `Role #${id}`);
};

/** Roles marked REVOKE in decisions (includes name even if roles_to_remove empty). */
export const revokedRoleLabels = (item) => {
    const fromRemove = rolesToRemoveLabels(item);
    if (fromRemove.length) return fromRemove;
    return (item?.role_decisions || []).filter((d) => d.decision === ROLE_DECISION.REVOKE).map((d) => d.roleName || d.role_name || `Role #${d.roleId ?? d.role_id}`);
};

/** KEEP/REVOKE tallies from the manager's recorded decisions. */
export const roleDecisionSummary = (item) => {
    const decisions = item?.role_decisions || [];
    let kept = 0;
    let revoked = 0;
    decisions.forEach((d) => {
        if (d.decision === ROLE_DECISION.REVOKE) revoked += 1;
        else if (d.decision === ROLE_DECISION.KEEP) kept += 1;
    });
    return { kept, revoked, total: decisions.length };
};

/** MODIFY / DELETE — precomputed by the backend, derived when the row omits it. */
export const resolveRequestType = (item) => item?.request_type || (item?.proposed_offboard ? "DELETE" : "MODIFY");

export const REVIEWER_SOURCE = {
    MANAGER: "MANAGER",
    NO_MANAGER: "NO_MANAGER",
    MANAGER_DEPARTED: "MANAGER_DEPARTED",
    SELF: "SELF",
    REASSIGNED: "REASSIGNED",
};

export const REVIEWER_SOURCE_META = {
    MANAGER: { label: "Manager", tone: "slate", needsAdmin: false },
    NO_MANAGER: { label: "No manager", tone: "amber", needsAdmin: false },
    MANAGER_DEPARTED: { label: "Manager left", tone: "red", needsAdmin: true },
    SELF: { label: "Self-review", tone: "red", needsAdmin: true },
    REASSIGNED: { label: "Reassigned", tone: "blue", needsAdmin: false },
};

/** @deprecated use REVIEWER_SOURCE_META */
export const REVIEWER_SOURCE_LABEL = {
    MANAGER: "Assigned manager",
    NO_MANAGER: "No manager (fallback reviewer)",
    MANAGER_DEPARTED: "Manager departed (needs reassignment)",
    SELF: "Self-review (needs reassignment)",
    REASSIGNED: "Reassigned reviewer",
};

const ACTIVE_RAISED_REQUEST = new Set(["PENDING_APPROVAL", "PENDING", "IN_PROGRESS", "SUBMITTED", "OPEN", "APPROVED_PENDING"]);
const REJECTED_RAISED_REQUEST = new Set(["REJECTED", "CANCELLED", "CANCELED"]);

/** Status of the GM-5299 request linked from this item, if present. */
export const getRaisedRequestStatus = (item) => {
    if (!item) return null;
    const raw = item.raised_request_status || item.linked_request_status || item.request_status || item.raised_request?.status || item.linked_request?.status || null;
    return raw ? String(raw).toUpperCase() : null;
};

/** True while a raised lifecycle request is still in flight (blocks re-review). */
export const isRaisedRequestLive = (item) => {
    if (!item) return false;
    const status = getRaisedRequestStatus(item);
    if (status) return ACTIVE_RAISED_REQUEST.has(status);
    // Fallback: id present + still CHANGE_REQUESTED usually means the request is live.
    return item.raised_request_id != null && item.review_status === ITEM_STATUS.CHANGE_REQUESTED;
};

/** Prior CR was rejected/cancelled — item is actionable again without HR reopen. */
export const isReReviewAfterRejectedRequest = (item) => {
    if (!item) return false;
    const status = getRaisedRequestStatus(item);
    if (status && REJECTED_RAISED_REQUEST.has(status)) return true;
    return !!(item.previous_request_rejected || item.can_re_review || item.re_review_allowed);
};

/**
 * Whether the assigned reviewer may submit POST /review.
 * PENDING → yes; decided → no (HR must reopen); live CR → no; rejected CR → yes again.
 */
export const isItemEditableByReviewer = (item) => {
    if (!item) return false;
    if (isRaisedRequestLive(item)) return false;
    if (isReReviewAfterRejectedRequest(item)) return true;
    return item.review_status === ITEM_STATUS.PENDING;
};

export const reviewerEditLockReason = (item) => {
    if (!item) return null;
    if (isRaisedRequestLive(item)) {
        const id = item.raised_request_id;
        return id != null ? `A change request (#${id}) is already raised. Reject or cancel it in User Lifecycle before reviewing again.` : "A change request is already raised. Reject or cancel it before reviewing again.";
    }
    if (item.review_status === ITEM_STATUS.APPROVED || item.review_status === ITEM_STATUS.CHANGE_REQUESTED) {
        return "This decision is locked. Ask HR to reopen it if the decision needs to change.";
    }
    return null;
};

export const needsAdminReassign = (item) => {
    const src = item?.reviewer_source;
    return src === REVIEWER_SOURCE.SELF || src === REVIEWER_SOURCE.MANAGER_DEPARTED;
};

export const progressPct = (progress) => {
    if (!progress) return 0;
    const total = Number(progress.total_items) || 0;
    if (!total) return 0;
    const done = (Number(progress.approved_count) || 0) + (Number(progress.change_requested_count) || 0);
    return Math.min(100, Math.round((done / total) * 100));
};

/** Normalize progress fields from nested or flat / snake or camel payloads. */
export const normalizeProgress = (reviewOrProgress) => {
    if (!reviewOrProgress || typeof reviewOrProgress !== "object") return null;
    const p = reviewOrProgress.progress && typeof reviewOrProgress.progress === "object" ? reviewOrProgress.progress : reviewOrProgress;
    const total = p.total_items ?? p.totalItems;
    const pending = p.pending_count ?? p.pendingCount;
    const approved = p.approved_count ?? p.approvedCount;
    const change = p.change_requested_count ?? p.changeRequestedCount;
    const raised = p.change_requests_raised ?? p.changeRequestsRaised;
    const privilegedPending = p.privileged_pending_count ?? p.privilegedPendingCount;
    if (total == null && pending == null && approved == null && change == null) return null;
    return {
        total_items: total,
        pending_count: pending,
        approved_count: approved,
        change_requested_count: change,
        change_requests_raised: raised,
        privileged_pending_count: privilegedPending,
    };
};

/** True when progress has a real population (not empty / all-zero placeholder). */
export const hasUsableProgress = (progress) => {
    if (!progress) return false;
    const total = Number(progress.total_items);
    if (Number.isFinite(total) && total > 0) return true;
    const sum = (Number(progress.pending_count) || 0) + (Number(progress.approved_count) || 0) + (Number(progress.change_requested_count) || 0);
    return sum > 0;
};

/** Rows per request for the paginated listings. */
export const PAGE_SIZE = 20;

/**
 * Read a paginated list payload — `{ content, totalItems, totalPages, currentPage }`,
 * tolerating the plain-array and `data`/`items` shapes older endpoints return.
 */
export const parseListResponse = (res, { page = 1, size = PAGE_SIZE } = {}) => {
    const data = res?.data;
    const raw = Array.isArray(data) ? data : data?.content || data?.data || data?.items || data?.reviews || [];
    const items = Array.isArray(raw) ? raw : [];
    const totalItems = Number(data?.totalItems ?? data?.total_items ?? data?.totalElements);
    const totalPages = Number(data?.totalPages ?? data?.total_pages);
    const reported = Number(data?.currentPage ?? data?.current_page);
    const currentPage = Number.isFinite(reported) && reported > 0 ? reported : page;
    // Without page metadata, assume a full page means there may be another one.
    const hasMore = Number.isFinite(totalPages) && totalPages > 0 ? currentPage < totalPages : items.length >= size;
    return {
        items,
        totalItems: Number.isFinite(totalItems) ? totalItems : null,
        totalPages: Number.isFinite(totalPages) ? totalPages : null,
        currentPage,
        // A server that echoes back a different page is ignoring `page` — stop paging
        // rather than appending the same rows forever.
        hasMore: hasMore && currentPage === page,
    };
};

/** Treat missing preflight user arrays as empty (backend NON_NULL omission). */
export const preflightUserList = (value) => (Array.isArray(value) ? value : []);

/** Strip deleted_<epoch>_ prefix from soft-deleted manager usernames for display. */
export const stripDeletedUsernamePrefix = (username) => {
    if (!username) return "";
    const match = String(username).match(/^deleted_\d+_(.+)$/);
    return match ? match[1] : String(username);
};

/** Build initial KEEP map from DIRECT snapshot roles only (group roles are not decided). */
export const initialRoleDecisions = (snapshot) => {
    const { direct } = snapshotRoles(snapshot);
    const map = {};
    direct.forEach((r) => {
        if (r?.id != null) map[r.id] = ROLE_DECISION.KEEP;
    });
    return map;
};

/** Payload for POST /review — only DIRECT roles; group-derived must never be sent as REVOKE. */
export const roleDecisionsPayload = (decisionMap, snapshot) => {
    const { direct } = snapshotRoles(snapshot);
    const directIds = new Set(direct.map((r) => Number(r.id)));
    return Object.entries(decisionMap || {})
        .filter(([roleId, d]) => directIds.has(Number(roleId)) && (d === ROLE_DECISION.REVOKE || d === ROLE_DECISION.KEEP))
        .map(([roleId, decision]) => ({ role_id: Number(roleId), decision }));
};

/** Plain approve-all body — used for quick Accept from the queue listing. */
export const buildQuickAcceptReviewBody = (snapshot) => ({
    role_decisions: roleDecisionsPayload(initialRoleDecisions(snapshot), snapshot),
    offboard: false,
});

/** True when the reviewer changed anything from a default approve-all. */
export const reviewHasChanges = ({ decisions, correctManager, offboard, note }) => {
    if (offboard || correctManager) return true;
    if (String(note || "").trim()) return true;
    return Object.values(decisions || {}).some((d) => d === ROLE_DECISION.REVOKE);
};

export const reviewSubmitButtonLabel = ({ hasChanges, busy }) => {
    if (busy) return hasChanges ? "Submitting…" : "Accepting…";
    return hasChanges ? "Submit" : "Accept";
};

/** Success toast after POST /review based on outcome. */
export const reviewSubmitSuccessMessage = ({ offboard, correctManager, decisions }) => {
    const revoked = Object.values(decisions || {}).includes(ROLE_DECISION.REVOKE);
    return offboard || correctManager || revoked ? "Change requested" : "Access approved";
};
